import React from 'react';
import moment from 'moment-timezone';
import { message, Modal, notification } from 'antd';
import Immutable, { fromJS, List, Map } from 'immutable';
import { call, put, select } from 'redux-saga/effects';
import { convertNodeToElement } from 'react-html-parser';

import { authAliasSelector, authTokenSelector, timezoneSelector } from 'selectors/authSelectors';
import { backendUrl, dateFormat, locale, defaultTimezone, aliasHeader } from 'util/config';
import { store } from 'MooTeam';
import AppLocale from 'lngProvider';
import { hideLoader, toggleAppError } from 'actions/settingActions';
import RbacRuleFactory from 'rbac/RbacRuleFactory';
import { getUserPermissions } from 'selectors/entities/rbacSelectors';
import { TASK } from 'constants/RbacPermissions';
import DropdownMenu from 'components/ui/DropdownMenu/DropdownMenu';
import Icon from 'components/common/Icon';

import { KJUR, b64utoutf8 } from 'jsrsasign';
import { unauthorized } from 'actions/authActions';
import { DETECT_CHANGE_WORKSPACE, SIGNOUT_USER_SUCCESS, UNAUTHORIZED } from 'constants/ActionTypes';
import { deleteCookie } from './cookieHelpers';
import { AUTH_COOKIE } from '../constants/UtilConstants';
import { ENTITY_FILE, ENTITY_PROJECT, ENTITY_USER, showLimitNotification } from './companyHelpers';

moment.tz.setDefault(defaultTimezone);

export const LOGOUT_ACTION_TYPES = [SIGNOUT_USER_SUCCESS, UNAUTHORIZED, DETECT_CHANGE_WORKSPACE];

// Function displays specified error message at the top of the page.
export const showErrorMessage = (errorMessage) => {
  message.error(errorMessage, 5);
};

// Function displays specified success message at the top of the page.
export const showSuccessMessage = (successMessage) => {
  message.success(successMessage, 5);
};

/**
 * Get default settings for pagination component
 * @param pagination
 * @param options
 * @returns {{current: *, total: *, showTotal: (function(*=, *): string), hideOnSinglePage: boolean, defaultCurrent: *, pageSize: *}}
 */
export const getPagination = (pagination, options = {}) => {
  const paginationTemplate = getIntlMessage(options.template ? options.template : 'ui.pagination.template');
  return {
    hideOnSinglePage: true,
    defaultCurrent: pagination?.get('currentPage'),
    pageSize: pagination?.get('perPage'),
    current: pagination?.get('currentPage'),
    total: pagination?.get('totalCount'),
    showTotal: (num, range) => {
      const replaceMap = {
        start: range[0],
        end: range[1],
        all: num || 0,
      };
      return paginationTemplate.replace(/start|end|all/gi, (matched) => {
        return replaceMap[matched];
      });
    },
    ...options,
  };
};

// Function displays modal view for confirm action.
export const showConfirm = (props = {}) => {
  const { confirm } = Modal;
  confirm({
    title: getIntlMessage('message.confirm'),
    okText: getIntlMessage('button.confirm'),
    cancelText: getIntlMessage('button.cancel'),
    okType: 'danger',
    onOk: (close) => {
      if (props.okAction) {
        props.okAction();
      }
      close();
    },
    onCancel: (close) => {
      if (props.cancelAction) {
        props.cancelAction();
      }
      close();
    },
    ...props,
  });
};

// Props for sending a file to the server
export const uploadAvatarProps = (userId) => {
  return {
    action: backendUrl + `/api/v2/public-files`,
    headers: {
      Authorization: 'Bearer ' + authTokenSelector(store.getState()),
      [aliasHeader]: authAliasSelector(store.getState()),
    },
    data: {
      userId: userId,
    },
  };
};

/**
 * Function returns the difference in days between the received date and the current
 * @param comparedDate
 * @param inSeconds
 * @returns {number}
 */
export const compareDate = (comparedDate, inSeconds = false, inMinutes = false) => {
  const currentDate = new Date();
  comparedDate = new Date(comparedDate);
  const delta = comparedDate.getTime() - currentDate.getTime();
  const x = inSeconds ? delta / 1000 : inMinutes ? delta / 1000 / 60 : delta / 1000 / 60 / 60 / 24;
  if (delta < 0) {
    return Math.ceil(x);
  } else {
    return Math.floor(x);
  }
};

/**
 * Return result the compare dates.
 * Ascendant sorting.
 * @param firstDate
 * @param secondDate
 * @param format
 * @returns {number}
 */
export const sortDate = (firstDate, secondDate, format = dateFormat) => {
  const first = moment(firstDate, format);
  const second = moment(secondDate, format);

  return first.isAfter(second) ? 1 : -1;
};

/**
 * Return result of sorting strings.
 */
export const sortString = (firstString, secondString, asc = true) => {
  const first = firstString ? firstString.toLowerCase() : '';
  const second = secondString ? secondString.toLowerCase() : '';

  if (asc) {
    return first < second ? -1 : 1;
  }
  return first > second ? -1 : 1;
};

/**
 * Returns the result of matching a search value with a value
 * @param value
 * @param search
 * @returns {boolean|*}
 */
export const filterSearch = (value, search) => {
  if (!search) {
    return true;
  }

  return value?.toLowerCase()?.indexOf(search.toLowerCase()) !== -1;
};

/**
 * Function set or get value in localStorage for system message popup
 * @param localStorageKey
 * @param operation
 * @returns {number}
 */
export const systemMessageHidden = (localStorageKey, operation = 'get') => {
  localStorageKey = `${authAliasSelector(store.getState())}-${localStorageKey}`;
  const checked = localStorage.getItem(localStorageKey);
  if (operation === 'get') {
    if (checked === null || checked === 'undefined' || !['0', '1'].includes(checked)) {
      return 1;
    }
    return parseInt(checked);
  }

  if (operation === 'set') {
    if (checked === null || checked === 'undefined' || !['0', '1'].includes(checked)) {
      localStorage.setItem(localStorageKey, '0');
    } else {
      localStorage.setItem(localStorageKey, parseInt(checked) ? '0' : '1');
    }
  }
};

/**
 * View system notification
 * @param content
 * @param options
 */
export const systemDangerNotification = (content, options = {}) => {
  notification.info({
    placement: 'topRight',
    duration: null,
    className: 'system-message',
    message: <>{content}</>,
    ...options,
  });
};

/**
 * Function fetch specified resource from backend API
 * @param resource    Name of the resource in api. Example: 'user-departments'.
 * @param method      Request method
 * @param bodyParams  Body params for the request (not available for GET and HEAD)
 * @param headers     Additional headers for the request
 * @param queryParams Query params of the request
 * @param apiModule   Module of the api
 */
export const fetchResource = async (
  resource,
  method = 'GET',
  bodyParams = {},
  headers = {},
  queryParams = {},
  apiModule = 'api',
) => {
  const apiUrl = new URL(backendUrl + '/' + apiModule + '/' + resource);
  apiUrl.search = new URLSearchParams(queryParams);

  const token = authTokenSelector(store.getState());
  const alias = authAliasSelector(store.getState());

  // Request initial settings
  const init = {
    method: method,
    headers: {
      ...headers,
      'Content-type': 'application/json',
    },
  };

  // Add authorization token
  if (token) {
    init.headers['Authorization'] = 'Bearer ' + token;
  }

  // Добавляем заголовок с alias-ом компании, если он есть
  if (alias) {
    init.headers[aliasHeader] = alias;
  }

  // Set request body params
  if (method !== 'GET' && method !== 'HEAD') {
    init.body = JSON.stringify(bodyParams);
  }

  // Fetch will fail only if there are network errors
  // All other errors should be handled in `then`
  return fetch(apiUrl, init).then(async (res) => {
    switch (res.status) {
      case 200:
      case 201: {
        if (res.headers.get('Content-Type').indexOf('application/json') !== -1) {
          return res.json();
        }

        return res.blob();
      }
      case 204:
        return true;
      case 401:
        deleteCookie(AUTH_COOKIE);
        store.dispatch(unauthorized());
        throw await res;
      case 403:
      case 404:
      case 422:
        throw await res;
      default:
        throw await res;
    }
  });
};

/**
 * Function normalize data requested from api.
 * @param entities    Array with models
 * @param idAttribute Id attribute of the following entities
 * @param stateEntities Entities from state (Immutable map object)
 * @param excludingFields fields to exclude
 * @returns Map
 */
export const normalizeEntities = (
  entities,
  idAttribute,
  stateEntities = new Map(),
  excludingFields = null,
  params = {},
) => {
  const { skipFields = [], deepMerge } = params;
  if (entities) {
    entities.forEach((entity) => {
      const entityId = entity[idAttribute];
      let stateEntity = stateEntities.get(parseInt(entityId));
      if (!stateEntity) {
        stateEntity = stateEntities.get(entityId);
      }
      if (stateEntity) {
        Object.keys(entity).forEach((field) => {
          if (!excludingFields || !excludingFields.includes(field)) {
            if (entity[field] && typeof entity[field] === 'object') {
              if (typeof deepMerge === 'object' && deepMerge[field]) {
                stateEntity = stateEntity.set(
                  field,
                  normalizeEntities(entities[field], deepMerge[field], stateEntity.get(field)),
                );
              } else {
                stateEntity = stateEntity.set(field, fromJS(entity[field]));
              }
              return;
            }

            stateEntity = stateEntity.set(field, entity[field]);
          }
        });
        stateEntities = stateEntities.set(entityId, stateEntity);
      } else {
        let preparedEntity = fromJS(entity);
        if (skipFields.length) {
          skipFields.forEach((field) => {
            preparedEntity = preparedEntity.delete(field);
          });
        }
        stateEntities = stateEntities.set(entityId, preparedEntity);
      }
    });
    return stateEntities;
  }
  return stateEntities;
};

/**
 * Function for merge response entity with state entity
 * @param entity response entity
 * @param stateEntity state entity
 * @param excludingFields fields to exclude
 * @returns {*}
 */
export const mergeEntity = (entity, stateEntity = new Map(), excludingFields = null) => {
  Object.keys(entity).forEach((field) => {
    if (!excludingFields || !excludingFields.includes(field)) {
      if (entity[field] && typeof entity[field] === 'object') {
        stateEntity = stateEntity.set(field, fromJS(entity[field]));
        return;
      }

      stateEntity = stateEntity.set(field, entity[field]);
    }
  });

  return stateEntity;
};

let permissions = null;
/**
 * Returns all current user permissions from state
 * @returns Map
 */
const getPermissions = () => {
  if (!permissions || !permissions.size) {
    permissions = getUserPermissions(store.getState());
  }

  return permissions;
};

export const clearPermissions = () => (permissions = null);

/**
 * Checks if current user have right permission
 *
 * @param permissionName
 * @param executeParams Params for the permission rule, if it has one
 * @returns {boolean} If user have the permission
 */
export const checkPermission = (permissionName, executeParams = {}) => {
  let allowed = false;
  const permission = getPermissions().get(permissionName);

  // If user have permission
  if (permission) {
    // Checking business rule
    if (permission.get('rule')) {
      const rule = RbacRuleFactory.createRule(permission.get('rule'));
      if (!rule?.execute) {
        return false;
      }
      allowed = rule.execute(executeParams);
    } else {
      // If rule is empty, than we go next and check parents
      allowed = true;
    }

    if (allowed) {
      if (!permission.get('parents')) {
        return true;
      } else {
        for (const parent of permission.get('parents')) {
          if (!parent.size) {
            // if parent is an empty array than permission has direct access to the one of the role groups
            return true;
          } else {
            // else check parent permission recursively
            allowed = checkPermission(parent.first(), executeParams);
          }
        }
      }
    }
  }

  return allowed;
};

/**
 * Function returns internalize message by its id
 * @param id
 * @returns string|array
 */
export function getIntlMessage(id) {
  if (!store) {
    return '';
  }

  const alias = authAliasSelector(store.getState());
  const companyLanguage = localStorage.getItem(`${alias}-language`);
  const currentLocale = AppLocale[companyLanguage && companyLanguage !== 'undefined' ? companyLanguage : locale.locale];
  return currentLocale.messages[id];
}

export const getIntlMessageComponent = (id, components = {}, renderParams = { component: 'span' }) => {
  let replaced = getIntlMessage(id);

  if (!replaced || typeof replaced !== 'string') {
    return null;
  }

  const renderedComponents = [];
  Object.keys(components).forEach((key) => {
    const regexp = new RegExp(`({@${key}})([\\s\\S]+?)({/@})`, 'gi');
    replaced = replaced.replace(regexp, (match, componentName, content) => {
      const component = components[componentName.split(/[{|}|@]/gi).join('')];
      if (component && typeof component === 'function') {
        renderedComponents.push(component(content));
        return '{component}';
      }
      return '';
    });
  });

  return replaced.split('{component}').map((string, index) => (
    <React.Fragment key={index}>
      {string !== '' ? React.createElement(renderParams.component, renderParams.props, string) : null}
      {renderedComponents[index]}
    </React.Fragment>
  ));
};

/**
 * Function return name for icon by currency
 * @param currency
 * @returns string
 */
export const getCurrencyIcon = (currency) => {
  const currencies = {
    RUB: 'ruble-sign',
    UAH: 'hryvnia',
    BYN: 'usd-circle',
    USD: 'dollar-sign',
    EUR: 'euro-sign',
    KZT: 'tenge',
  };

  return currencies[currency];
};

/**
 * Function returns parted intl message as one node.
 * @param id
 * @returns {string|*}
 */
export const getIntlMessageParts = (id) => {
  const parts = getIntlMessage(id);

  if (Array.isArray(parts)) {
    return parts.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  }
  return parts;
};

/**
 * Function loads entities from specified resource.
 * @param action Redux action, which should include:
 *               payload: {
 *                 resource - name of the api resource
 *                 onSuccess - action which will be dispatched if request is succeed
 *                 onErrorMessage - message which will be shown if request ends up with error
 *
 *                 optional:
 *                 page - number of a loaded page, if not specified loads first page.
 *                 extraFields - array with names of the all extra fields of the model from api.
 *                 filters - object which keys is name of the filter and value is a selector
 *               }
 */
export function* loadEntities(action) {
  document.body.style.cursor = 'wait';

  let queryParams = {};
  if (action.payload.queryParams) {
    queryParams = action.payload.queryParams;
  }

  // Sets page which needs to be loaded
  if (action.payload.page) {
    queryParams['page'] = action.payload.page;
  }

  // Sets count items per page (0 - all items)
  if (action.payload.perPage || action.payload.perPage === 0) {
    queryParams['per-page'] = action.payload.perPage;
  }

  // Setup additional fields which will be in the resulted response
  if (action.payload.extraFields) {
    queryParams['expand'] = action.payload.extraFields.join();
  }

  // Setup fields which will be in the resulted response
  if (action.payload.fields) {
    queryParams['fields'] = action.payload.fields.join();
  }

  const applaySort = (sortValue, queryParams, isAdditional) => {
    let value = '';
    if (isAdditional && queryParams['sort']) {
      value = queryParams['sort'] + ',';
    }
    if (sortValue && sortValue.get('value') === 'ascending') {
      value += sortValue.get('key');
    }
    if (sortValue && sortValue.get('value') === 'descending') {
      value += '-' + sortValue.get('key');
    }
    queryParams['sort'] = value;
  };

  // Setup sort
  if (action.payload.sort) {
    if (typeof action.payload.sort === 'string') {
      queryParams['sort'] = action.payload.sort;
    } else {
      const sortValue = yield select(action.payload.sort);
      if (Immutable.isList(sortValue)) {
        sortValue.forEach((sorting, index) => {
          applaySort(sorting, queryParams, index > 0);
        });
      } else {
        applaySort(sortValue, queryParams);
      }
    }
  }

  // Setup filtration
  if (action.payload.filters) {
    // Go through each filter
    for (const key of Object.keys(action.payload.filters)) {
      const filter = action.payload.filters[key];
      let filterValue;
      if (filter['selector']) {
        // Call filter selector
        filterValue = yield select(filter['selector']);
      } else {
        filterValue = filter['value'];
      }
      if (filterValue) {
        const selectorKey = filter['key'];
        // If selector returns several filters, take only one key which is specified
        if (selectorKey) {
          const value = filterValue.get(selectorKey);
          // If value is specified and doesn't equal value which needs to be skip
          if (value && value !== filter.skipValue) {
            queryParams['filters[' + key + ']'] = value;
          }
        } else {
          queryParams['filters[' + key + ']'] = filterValue;
        }
      } else if (filter.defaultValue) {
        queryParams['filters[' + key + ']'] = filter.defaultValue;
      }
    }
  }

  if (action.payload.prepareQueryParams) {
    queryParams = action.payload.prepareQueryParams(queryParams);
  }
  const onLoading = action.payload.onLoading;
  try {
    if (onLoading) {
      onLoading(true);
    }

    const response = yield call(fetchResource, action.payload.resource, 'GET', {}, {}, queryParams);

    if (response) {
      if (action.payload.onSuccess) {
        yield put(action.payload.onSuccess(response, action));
      }

      if (action.payload.onSuccessFunction) {
        action.payload.onSuccessFunction(response, action);
      }

      if (action.payload.afterSuccess) {
        yield put(action.payload.afterSuccess(response));
      }

      if (onLoading) {
        onLoading(false);
      }

      if (action.payload.onSuccessMessage) {
        showSuccessMessage(action.payload.onSuccessMessage);
      }
    }

    if (action.payload.hideLoader) {
      yield put(hideLoader());
    }
  } catch (response) {
    if (response && navigator.onLine) {
      try {
        const data = yield response.json();

        if (response.status) {
          if (action.payload.onError) {
            yield put(action.payload.onError(response.status, data));
          } else if ([403, 404, 500].includes(response.status)) {
            if (!action.payload.disableAppError) {
              yield put(toggleAppError(response.status, data));
            }
          }
        }
      } catch (error) {
        console.log(action.type); // eslint-disable-line no-console
        console.log(error); // eslint-disable-line no-console
      }
    }

    if (action.payload.onErrorMessage) {
      showErrorMessage(action.payload.onErrorMessage);
    }

    if (action.payload.onErrorFunction) {
      action.payload.onErrorFunction(response, action);
    }

    if (onLoading) {
      onLoading(false);
    }

    yield put(hideLoader());
  }

  document.body.style.cursor = 'default';
}

/**
 * Function sends request to update or create entity,
 * and performs additional actions on successful request.
 *
 * @param action Redux action, which should contain:
 *               payload: {
 *                 resource - name of the api resource
 *                 onSuccess - action which will be dispatched if request is succeed
 *                 entityPrimaryKey - id of the entity, used for the update request
 *
 *                 optional params:
 *                 afterSuccess - additional action which will be dispatched after success
 *                 onSuccessMessage - message which will be shown if request is succeed
 *                 onErrorMessage - message which will be shown if request is failed
 *               }
 */
export function* updateEntity(action) {
  let resolve, reject;
  if (action.promise) {
    resolve = action.promise.resolve;
    reject = action.promise.reject;
  }
  const primaryKey = action.payload.entityPrimaryKey;

  // Decide if it's an update or create request
  let resource = action.payload.resource;
  let method = 'POST';
  let queryParams = {};
  if (action.payload.queryParams) {
    queryParams = action.payload.queryParams;
  }
  if (primaryKey && action.values[primaryKey]) {
    method = 'PUT';
    resource += '/' + action.values[primaryKey];
  }

  if (action.payload.method) {
    method = action.payload.method;
  }

  if (action.payload.extraFields && action.payload.extraFields.length) {
    queryParams['expand'] = action.payload.extraFields.join();
  }

  // Setup filtration
  if (action.payload.filters) {
    // Go through each filter
    for (const key of Object.keys(action.payload.filters)) {
      const filter = action.payload.filters[key];
      let filterValue;
      if (filter['selector']) {
        // Call filter selector
        filterValue = yield select(filter['selector']);
      } else {
        filterValue = filter['value'];
      }
      if (filterValue) {
        const selectorKey = filter['key'];
        // If selector returns several filters, take only one key which is specified
        if (selectorKey) {
          const value = filterValue.get(selectorKey);
          // If value is specified and doesn't equal value which needs to be skip
          if (value && value !== filter.skipValue) {
            queryParams['filters[' + key + ']'] = value;
          }
        } else {
          queryParams['filters[' + key + ']'] = filterValue;
        }
      }
    }
  }

  const onLoading = action.payload.onLoading;
  try {
    if (onLoading) {
      onLoading(true);
    }
    if (action.payload.beforeRequest) {
      yield put(action.payload.beforeRequest(action));
    }
    if (action.payload.beforeRequestFunction) {
      action.payload.beforeRequestFunction(action);
    }

    const response = yield call(fetchResource, resource, method, { ...action.values }, {}, queryParams);

    if (action.payload.onSuccess) {
      yield put(action.payload.onSuccess(response, action));
    }

    if (onLoading) {
      onLoading(false);
    }

    if (action.payload.onSuccessFunction) {
      action.payload.onSuccessFunction(response, action);
    }

    if (action.payload.afterSuccess) {
      if (typeof action.payload.afterSuccess === 'function') {
        yield put(action.payload.afterSuccess(response, action));
      } else {
        for (let i = 0; i < action.payload.afterSuccess.length; i++) {
          yield put(action.payload.afterSuccess[i](response, action));
        }
      }
    }

    if (resolve) {
      resolve();
    }

    if (action.payload.onSuccessMessage) {
      showSuccessMessage(action.payload.onSuccessMessage);
    }

    if (action.payload.hideLoader) {
      yield put(hideLoader());
    }
  } catch (response) {
    if (response && !(response instanceof Error)) {
      const data = yield response.json();

      if (response.status) {
        if (action.payload.onError) {
          yield put(action.payload.onError(response.status, data));
        }

        if (action.payload.onErrorFunction) {
          action.payload.onErrorFunction(response.status, data);
        }

        const isErrorAction = action.payload.onError || action.payload.onErrorFunction;

        if (!isErrorAction && [403, 404, 500].includes(response.status)) {
          yield put(toggleAppError(response.status, data));
        }

        if (onLoading) {
          onLoading(false);
        }

        if (response.status === 422 && errorLimitResponse(data)) {
          return;
        }

        if (reject && response.status === 422) {
          yield put(hideLoader());

          reject(data);
          return;
        }
      }
    } else {
      console.log(response); // eslint-disable-line no-console
    }

    if (action.payload.onErrorMessage) {
      showErrorMessage(action.payload.onErrorMessage);
    }

    yield put(hideLoader());
  }
}

/**
 * Function sends the request to delete entity.
 * @param action Redux action, which should contain:
 *               payload: {
 *                 resource - name of the api resource
 *                 entityId - id of the entity, used for the delete request
 *                 onSuccess - action which will be dispatched if request is succeed
 *
 *                 optional params:
 *                 onSuccessMessage - message which will be shown if request is succeed
 *                 onErrorMessage - message which will be shown if request is failed
 *               }
 */
export function* deleteEntity(action) {
  const resource = action.payload.resource;
  const entityId = action.payload.entityId;
  const queryParams = action.payload.queryParams;

  try {
    yield call(fetchResource, resource + '/' + entityId, 'DELETE', {}, {}, queryParams);

    if (action.payload.onSuccess) {
      yield put(action.payload.onSuccess(entityId, action.payload.props));
    }

    if (action.payload.afterSuccess) {
      yield put(action.payload.afterSuccess(entityId, action.payload.props));
    }

    if (action.payload.onSuccessFunction) {
      action.payload.onSuccessFunction(action);
    }

    if (action.payload.onSuccessMessage) {
      showSuccessMessage(action.payload.onSuccessMessage);
    }

    if (action.payload.hideLoader) {
      yield put(hideLoader());
    }
  } catch (response) {
    if (response && !(response instanceof Error)) {
      const data = yield response.json();

      if (response.status) {
        if (action.payload.onError || action.payload.onErrorFunction) {
          if (action.payload.onError) {
            yield put(action.payload.onError(response.status, data));
          }
          if (action.payload.onErrorFunction) {
            action.payload.onErrorFunction(response.status, data);
          }
        } else if ([403, 404, 500].includes(response.status)) {
          yield put(toggleAppError(response.status, data));
        }
      }
    } else {
      console.log(response); // eslint-disable-line no-console
    }

    if (action.payload.onErrorMessage) {
      yield showErrorMessage(action.payload.onErrorMessage);
    }

    yield put(hideLoader());
  }
}

/**
 * Метод обрабатывает ошибку запроса со статусом 422.
 * Создает нотификацию в случае ошибки по лимитам
 * @param data
 * @returns {boolean}
 */
export const errorLimitResponse = (data) => {
  const alias = authAliasSelector(store.getState());
  if (data?.limit?.message) {
    showErrorMessage(data.limit.message);
    return true;
  }
  if (data?.storageLimit?.message) {
    showLimitNotification(ENTITY_FILE, alias);
    return true;
  }
  if (data?.usersLimit?.message) {
    showLimitNotification(ENTITY_USER, alias);
    return true;
  }
  if (data?.projectsLimit?.message) {
    showLimitNotification(ENTITY_PROJECT, alias);
    return true;
  }

  return false;
};

// Helper function for the page scrolling
// Source: https://gist.github.com/andjosh/6764939#gistcomment-2404310
export const scrollTo = (element, to, duration) => {
  const start = element.scrollTop,
    change = to - start,
    startDate = +new Date();

  // t = current time
  // b = start value
  // c = change in value
  // d = duration
  const easeInOutQuad = (t, b, c, d) => {
    t /= d / 2;
    if (t < 1) {
      return (c / 2) * t * t + b;
    }
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  const animateScroll = () => {
    const currentDate = +new Date();
    const currentTime = currentDate - startDate;
    element.scrollTop = parseInt(easeInOutQuad(currentTime, start, change, duration));
    if (currentTime < duration) {
      requestAnimationFrame(animateScroll);
    } else {
      element.scrollTop = to;
    }
  };

  animateScroll();
};

// Handles transforming of the node to the dom element
export const transform = (node, index) => {
  if (node.type === 'tag' && node.name === 'iframe') {
    // Disable iframes to prevent videos in notification drawer
    return null;
  }
  if (node.type === 'tag' && node.name === 'a') {
    node.attribs.target = '_blank';
    return convertNodeToElement(node, index, transform);
  }
};

/**
 * Returns a file icon of the corresponding type.
 * @param extension file extension
 * @param type file type
 * @returns {string} icon type from ant.d components.
 */
export const resolveFileIcon = (extension, type) => {
  const fileType = type.split('/')[0];

  if (fileType === 'image') {
    return 'file-image';
  }

  switch (extension) {
    case 'pdf':
      return 'file-pdf';
    case 'doc':
    case 'docx':
      return 'file-word';
    case 'ppt':
    case 'pptx':
      return 'file-ppt';
    case 'xls':
    case 'xlsx':
      return 'file-excel';
    case 'rar':
    case 'zip':
      return 'file-archive';
    default:
      return 'file';
  }
};
/**
 * Return time amount.
 * @param startDate project start date
 * @param endDate project endDate date
 * @returns {string} time amount.
 */
export const getTimeAmount = (startDate, endDate, isDayCounter = false, isAbs = false) => {
  startDate = moment(startDate, dateFormat);
  endDate = endDate ? moment(endDate, dateFormat) : moment();

  const getValue = (value) => (isAbs ? Math.abs(value) : value);

  const diff = endDate.diff(startDate);
  if (isDayCounter) {
    return getValue(diff / (1000 * 60 * 60 * 24));
  }
  const duration = moment.duration(diff);
  const label = [];

  duration.years() && label.push(getValue(duration.years()) + getIntlMessage('project.cards.years'));
  duration.months() && label.push(getValue(duration.months()) + getIntlMessage('project.cards.months'));
  duration.days() && label.push(getValue(duration.days()) + getIntlMessage('project.cards.days'));
  return label.join(' ');
};

/**
 * Возвращает разницу между second и first датой
 * @param firstDate
 * @param secondDate
 * @param format
 * @returns {number} time amount.
 */
export const getRawMomentDiff = (firstDate, secondDate, format = dateFormat) => {
  const today = !firstDate || !secondDate ? moment(moment().format(format), format) : null;
  const first = firstDate ? moment(firstDate, format) : today;
  const second = secondDate ? moment(secondDate, format) : today;

  return second.diff(first);
};

/**
 * Return dayse title.
 * @param days days count
 * @returns {string} time amount.
 */
export const getDaysTitle = (days = 0) => {
  if ([11, 12, 13, 14].includes(days % 100)) {
    return getIntlMessage('task.days');
  }
  switch (days % 10) {
    case 1:
      return getIntlMessage('task.day');
    case 2:
    case 3:
    case 4:
      return getIntlMessage('task.twoDays');
    default:
      return getIntlMessage('task.days');
  }
};

// User mention config for CKEditor
export const mentionUserConfig = () => {
  const template =
    '<li data-id="{id}">' +
    '<span class="ant-avatar ant-avatar-circle ant-avatar-image gx-mr-2">' +
    '<img src="{avatar}"/>' +
    '</span>' +
    '<strong class="fullname">{fullName}</strong>' +
    '</li>';

  return {
    itemTemplate: template,
    outputTemplate: '<a data-user-id="{id}" href="{link}">@{fullName}</a><span>&nbsp;</span>',
    minChars: 0,
    pattern: /@[_a-zA-Z0-9А-Яа-я]*$/,
  };
};

/**
 * Check user access for editing tasks according to project settings
 *
 * @param task Checked task
 * @param user Verified user
 * @param permissions User permissions
 * @param permissionName Project settings key
 * @param checkTaskStatus Check task status for this field
 * @returns {boolean} If user have the access
 */
export const checkAccess = (task, user, permissions, permissionName = null, checkTaskStatus = true, team = null) => {
  if (!task) {
    return false;
  }

  const { canUpdate, canUpdateMeta } = permissions;
  const project = task.get('project');
  const isActiveTask = task.get('status') === 'active';

  const isClient = user.get('userType') === 'client';

  // Check project permission for client
  if (isClient && !project?.get('canClientUpdate')) {
    return false;
  }

  // Check leaders projects
  if (project?.get('team') || team) {
    let userTeam = team || project?.get('team');
    userTeam = userTeam.find((memberTeam) => memberTeam?.get('userId') === user?.get('userId'));
    if (['leader', 'responsible', 'manager'].includes(userTeam?.get('type'))) {
      return true;
    }
  }

  // Check admins and creator for this task
  if (canUpdate) {
    // Check access for taskFull
    if (checkPermission(TASK.delete)) {
      return true;
    }
    return checkTaskStatus ? isActiveTask : true;
  }

  // Check responsible for this task
  if (!canUpdateMeta) {
    return false;
  }

  // Check project permissions
  if (permissionName) {
    if (permissionName === 'canUserChangeDate' && project?.get('canUserChangeDate')) {
      return checkTaskStatus ? !isClient && isActiveTask : !isClient;
    }

    return checkTaskStatus ? !!project?.get(permissionName) && isActiveTask : !!project?.get(permissionName);
  }

  return false;
};

export const checkPrivacyUsers = (projectTeam, privacyUsers) => {
  let isPrivacy = false;
  if (projectTeam && privacyUsers) {
    const teamIds = projectTeam.map((member) => member.getIn(['user', 'userId']));
    const privacyIds = privacyUsers.map((user) => user?.get('userId') || user?.getIn(['user', 'userId']));

    if (privacyIds?.size) {
      isPrivacy = !!teamIds.find((id) => !privacyIds.includes(id));
    }
  }
  return isPrivacy;
};

// Function returns company logo
export const getCompanyLogo = (logo) => {
  return logo ? getPublicFileUrl(logo) : null;
};

// Get init moment-timezone or moment
export const getMomentTimezone = (data, format) => {
  try {
    const companyTimezone = timezoneSelector(store.getState());
    if (companyTimezone && moment) {
      const momentTimezone = moment(data, format).tz(companyTimezone);
      return momentTimezone ? momentTimezone : moment(data, format);
    }
  } catch (e) {
    console.log(e); // eslint-disable-line no-console
  }

  return moment(data, format);
};

// Get response code color class
export const getStatusColor = (code) => {
  switch (code?.toString()[0]) {
    case '2':
      return 'gx-text-primary';
    case '3':
      return 'warning-status-code';
    default:
      return 'gx-text-red';
  }
};

// Get JWT access token
export const getJwtToken = (payload = {}, additionalHeader = {}) => {
  const header = JSON.stringify({ ...additionalHeader, alg: 'HS256' });
  return KJUR.jws.JWS.sign('HS256', header, JSON.stringify(payload), {
    b64: process.env.REACT_APP_JWT_SECRET,
  });
};

// Возвращает закодированный jwt.payload
export const getJwtPayload = (token) => {
  return KJUR.jws.JWS.readSafeJSONString(b64utoutf8(token.split('.')[1]));
};

// Save file from Blob
export const saveBlob = (blob, fileName) => {
  if (!(blob instanceof Blob)) {
    return;
  }
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style = 'display: none';

  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

/**
 * Function get parameter from search in history
 * @param name
 * @param isInteger
 * @returns {null|*}
 */
export const getQueryParam = (name, isInteger = false) => {
  const search = window.location?.search;
  let value = search ? new URLSearchParams(search).get(name) : null;
  return isInteger && value ? parseInt(value) : value;
};

export const existQueryHash = (location, name) => {
  const hash = location?.hash;
  return typeof hash === 'string' && hash.includes(name);
};

export const prepareDataByDataset = (entities, key, dataset, sort = 'asc') => {
  if (!entities || !dataset || !key) {
    return null;
  }

  sort = sort === 'asc' ? 1 : -1;
  return entities
    .filter((entity) => dataset.indexOf(entity.get(key)) !== -1)
    .sort((a, b) => {
      const aIndex = dataset.indexOf(a.get(key));
      const bIndex = dataset.indexOf(b.get(key));
      return aIndex > bIndex ? sort : -sort;
    });
};

export const getTable = (pattern, items, itemKey) => {
  if (!pattern || !items) {
    return {};
  }
  const columns = [];

  pattern.forEach((itemPattern) => {
    const { key, actions, allowed = true } = itemPattern;
    if (!allowed) {
      return;
    }

    if (key === 'menu') {
      columns.push({
        key: 'menu',
        width: '5%',
        render: (record) => {
          return (
            <DropdownMenu
              items={actions(record)}
              content={
                <span>
                  <Icon className="gx-icon-btn gx-d-flex" name="ellipsis-v" />
                </span>
              }
            />
          );
        },
      });
    } else {
      columns.push({
        ...itemPattern,
        dataIndex: key,
      });
    }
  });

  const dataSource = [];
  const dataKeys = pattern.filter((item) => item.allowed !== false).map((item) => item.key);

  items.forEach((item) => {
    let data = { key: item.get(itemKey), model: item };
    dataKeys.forEach((key) => {
      if (key !== 'menu') {
        data[key] = item.get(key);
      }
    });

    dataSource.push(data);
  });

  return { columns, dataSource };
};

export const copyToClipboard = (value, successMessage = '') => {
  if (!navigator.clipboard) {
    return;
  }

  navigator.clipboard
    .writeText(value)
    .then(() => {
      showSuccessMessage(successMessage);
    })
    .catch((error) => {
      console.log('Something went wrong', error); // eslint-disable-line no-console
    });
};

export const getFileListFromEntities = (entityMap) => {
  const entities = Object.values(entityMap);
  if (!entities.length) {
    return new List();
  }
  const files = [];
  entities.forEach((entity) => {
    if (entity.type !== 'IMAGE') {
      return;
    }
    const { file, name } = entity.data;
    if (file?.fileId) {
      files.push({ ...file, name, type: entity.type.toLowerCase() });
    }
  });

  return fromJS(files);
};

export const getPublicFileUrl = (token) => {
  if (!token) {
    return null;
  }
  return `${backendUrl}/api/v2/public-files/${token}`;
};
